import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  PlusIcon,
  SearchIcon,
  FilterIcon,
  CheckCircle2Icon,
  XCircleIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AppHeader } from '@/components/app-header'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'

interface Icd10Code {
  id: number
  code: string
  diagnosis_name: string
  category: string
  display_order: number
  active: boolean
  display_label: string
  created_at: string
  updated_at: string
}

interface AdminIcd10CodesIndexProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  icd10_codes: Icd10Code[]
  categories: string[]
  filters: {
    search: string | null
    category: string | null
    active: string | null
  }
}

export default function AdminIcd10CodesIndex({
  auth,
  icd10_codes,
  categories,
  filters,
}: AdminIcd10CodesIndexProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCode, setEditingCode] = useState<Icd10Code | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form state
  const [code, setCode] = useState('')
  const [diagnosisName, setDiagnosisName] = useState('')
  const [category, setCategory] = useState('')
  const [displayOrder, setDisplayOrder] = useState('0')

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(filters.search || '')
  const [selectedCategory, setSelectedCategory] = useState(filters.category || '')
  const [activeFilter, setActiveFilter] = useState(filters.active || '')

  const handleOpenCreateDialog = () => {
    setEditingCode(null)
    setCode('')
    setDiagnosisName('')
    setCategory('')
    setDisplayOrder('0')
    setDialogOpen(true)
  }

  const handleOpenEditDialog = (icd10Code: Icd10Code) => {
    setEditingCode(icd10Code)
    setCode(icd10Code.code)
    setDiagnosisName(icd10Code.diagnosis_name)
    setCategory(icd10Code.category)
    setDisplayOrder(icd10Code.display_order.toString())
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!code.trim() || !diagnosisName.trim() || !category.trim()) {
      toast.error('Code, diagnosis name, and category are required')
      return
    }

    setIsSubmitting(true)

    try {
      const url = editingCode
        ? `/admin/icd10_codes/${editingCode.id}`
        : '/admin/icd10_codes'
      const method = editingCode ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          icd10_code: {
            code: code.trim(),
            diagnosis_name: diagnosisName.trim(),
            category: category.trim(),
            display_order: parseInt(displayOrder) || 0,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success(editingCode ? 'Code updated successfully' : 'Code created successfully')
        router.reload()
        setDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to save code')
      }
    } catch (error) {
      toast.error('An error occurred while saving')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (icd10Code: Icd10Code) => {
    try {
      const response = await fetch(`/admin/icd10_codes/${icd10Code.id}/toggle_active`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      })

      const data = await response.json()

      if (data.success) {
        toast.success(icd10Code.active ? 'Code deactivated' : 'Code activated')
        router.reload()
      } else {
        toast.error('Failed to toggle status')
      }
    } catch (error) {
      toast.error('An error occurred')
    }
  }

  const handleDelete = async (icd10Code: Icd10Code) => {
    if (!confirm(`Delete code ${icd10Code.code}? This action cannot be undone.`)) {
      return
    }

    try {
      const response = await fetch(`/admin/icd10_codes/${icd10Code.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Code deleted successfully')
        router.reload()
      } else {
        toast.error('Failed to delete code')
      }
    } catch (error) {
      toast.error('An error occurred while deleting')
    }
  }

  const handleSearch = () => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('search', searchQuery)
    if (selectedCategory) params.set('category', selectedCategory)
    if (activeFilter) params.set('active', activeFilter)

    router.visit(`/admin/icd10_codes?${params.toString()}`)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setSelectedCategory('')
    setActiveFilter('')
    router.visit('/admin/icd10_codes')
  }

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <AppHeader breadcrumbs={[{ label: 'ICD-10 Codes' }]} />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">ICD-10 Code Management</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage diagnosis codes for the Problem List feature
          </p>
        </div>

        {/* Filters Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FilterIcon className="h-5 w-5" />
              Search & Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label>Search by code or diagnosis name</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="e.g., B18.2 or Hepatitis"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} size="icon">
                    <SearchIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label>Category</Label>
                <Select value={selectedCategory || 'all'} onValueChange={(val) => setSelectedCategory(val === 'all' ? '' : val)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select value={activeFilter || 'all'} onValueChange={(val) => setActiveFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="true">Active only</SelectItem>
                    <SelectItem value="false">Inactive only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(searchQuery || selectedCategory || activeFilter) && (
              <div className="mt-4">
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  Clear filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Codes Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>ICD-10 Codes</CardTitle>
                <CardDescription>{icd10_codes.length} codes</CardDescription>
              </div>
              <Button onClick={handleOpenCreateDialog}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Code
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {icd10_codes.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Code</TableHead>
                      <TableHead>Diagnosis Name</TableHead>
                      <TableHead className="w-[150px]">Category</TableHead>
                      <TableHead className="w-[100px]">Order</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[150px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {icd10_codes.map((icd10Code) => (
                      <TableRow key={icd10Code.id}>
                        <TableCell className="font-mono font-medium">{icd10Code.code}</TableCell>
                        <TableCell>{icd10Code.diagnosis_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{icd10Code.category}</Badge>
                        </TableCell>
                        <TableCell>{icd10Code.display_order}</TableCell>
                        <TableCell>
                          {icd10Code.active ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle2Icon className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <XCircleIcon className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenEditDialog(icd10Code)}
                              title="Edit"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleActive(icd10Code)}
                              title={icd10Code.active ? 'Deactivate' : 'Activate'}
                            >
                              {icd10Code.active ? (
                                <XCircleIcon className="h-4 w-4" />
                              ) : (
                                <CheckCircle2Icon className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(icd10Code)}
                              title="Delete"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No ICD-10 codes found</p>
                {(searchQuery || selectedCategory || activeFilter) && (
                  <Button variant="link" onClick={handleClearFilters} className="mt-2">
                    Clear filters to see all codes
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCode ? 'Edit ICD-10 Code' : 'Add ICD-10 Code'}</DialogTitle>
              <DialogDescription>
                {editingCode
                  ? 'Update the ICD-10 code information'
                  : 'Add a new diagnosis code to the system'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>ICD-10 Code *</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g., B18.2"
                  disabled={!!editingCode}
                />
              </div>

              <div className="space-y-2">
                <Label>Diagnosis Name *</Label>
                <Input
                  value={diagnosisName}
                  onChange={(e) => setDiagnosisName(e.target.value)}
                  placeholder="e.g., Chronic Hepatitis C, without Hepatic Coma"
                />
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hepatitis C">Hepatitis C</SelectItem>
                    <SelectItem value="Liver Disease">Liver Disease</SelectItem>
                    <SelectItem value="Comorbidities">Comorbidities</SelectItem>
                    <SelectItem value="Mental Health">Mental Health</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Lower numbers appear first in the dropdown
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !code || !diagnosisName || !category}
              >
                {isSubmitting ? 'Saving...' : editingCode ? 'Update Code' : 'Add Code'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
